import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { TScene } from '../../type/scene';
import { TLine } from '../../type/line';
import { useGameContext } from './GameContext';

interface IGameProps {
    targets?: TLine[];
    animationPromiseResolverRef: React.MutableRefObject<(() => void) | null>;
    editionMode?: boolean;
}

const IGame: React.FC<IGameProps> = ({
    animationPromiseResolverRef,
    editionMode = false,
}) => {

    const { state } = useGameContext();
    const { character, scene, initialTargetLines, targetLines } = state;

    const controls = useAnimation();

    useEffect(() => {
        // Calcul de la durée de transition en fonction de character.speed
        const speedDurationMap = {
            1: 1, // Lent
            2: 0.5, // Moyen
            3: 0.2, // Rapide
        };

        const durationTime = speedDurationMap[character.speed as 1 | 2 | 3] || 0.5;

        const durationStruct = {
            ...character.transition,
            duration: durationTime
        }
        controls
            .start({
                scaleX: character.scale,
                x: character.x,
                y: character.y,
                transition: durationStruct,
            })
            .then(() => {
                if (animationPromiseResolverRef.current) {
                    animationPromiseResolverRef.current();
                    animationPromiseResolverRef.current = null;
                }
            });

        // eslint-disable-next-line
    }, [character.x, character.y, character.scale]);

    // Précharger les images lors du montage du composant
    useEffect(() => {
        const imagesToPreload = [
            character.images.idle,
            character.images.moving,
            character.images.paused,
            character.images.colliding
        ];

        imagesToPreload.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, [character.images]);

    const [styleScene, setStyleScene] = useState<React.CSSProperties>({});

    useEffect(() => {
        setStyleScene(getBackgroundStyle(scene));
    }, [scene]);


    const getBackgroundStyle = (scene: TScene): React.CSSProperties => {
        const backgroundImages: string[] = [];
        const backgroundSizes: string[] = [];
        const backgroundRepeats: string[] = [];

        // Ajouter l'image de fond si elle est définie
        if (scene.background?.img) {
            backgroundImages.push(`url(${scene.background.img})`);
            backgroundSizes.push(scene.background.size || 'cover');
            backgroundRepeats.push(scene.background.repeat ? 'repeat' : 'no-repeat');
        }

        // Vérifier s'il y a des images d'arrière-plan à appliquer
        if (backgroundImages.length === 0) {
            return {
                backgroundImage: 'none',
            };
        } else {
            return {
                backgroundImage: backgroundImages.join(', '),
                backgroundSize: backgroundSizes.join(', '),
                backgroundRepeat: backgroundRepeats.join(', '),
            };
        }
    };
    const getCharacterImage = () => {
        switch (character.state) {
            case 'idle':
                return character.images.idle;
            case 'moving':
                return character.images.moving;
            case 'paused':
                return character.images.paused;
            case 'colliding':
                return character.images.colliding;
            case 'wining':
                return character.images.win;
        }
    };


    // Le trace est en pixels absolus : on zoome la scene entiere pour la faire
    // tenir dans la place disponible au lieu de la rogner.
    const fitRef = useRef<HTMLDivElement | null>(null);
    const [scale, setScale] = useState<number>(1);

    const computeScale = useCallback(() => {
        const container = fitRef.current;
        if (!container) return;

        const availableWidth = container.clientWidth;
        if (!availableWidth) return;

        const viewportHeight = window.innerHeight || scene.height;
        const heightRatio = window.innerWidth < 768 ? 0.42 : 0.62;
        const availableHeight = Math.max(220, viewportHeight * heightRatio);

        const next = Math.min(
            1,
            availableWidth / scene.width,
            availableHeight / scene.height
        );
        setScale(Number.isFinite(next) && next > 0 ? next : 1);
    }, [scene.width, scene.height]);

    useEffect(() => {
        computeScale();

        const container = fitRef.current;
        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(computeScale) : null;
        if (container) observer?.observe(container);
        window.addEventListener('resize', computeScale);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', computeScale);
        };
    }, [computeScale]);

    return (
        <div
            ref={fitRef}
            className="tilmi-stage-fit"
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                overflow: 'hidden',
            }}
        >
        <div
            style={{
                width: `${scene.width * scale}px`,
                height: `${scene.height * scale}px`,
                position: 'relative',
                flex: '0 0 auto',
            }}
        >
        <div
            id="sceneDraw"
            key="sceneDraw"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${scene.width}px`,
                height: `${scene.height}px`,
                backgroundColor: '#f9f9f9',
                border: '1px solid #ddd',
                boxSizing: 'border-box',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                ...styleScene,
            }}
        >
            {/* SVG pour tracer les lignes de mouvement */}
            <svg
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none', // Permet de ne pas interférer avec les interactions
                }}
            >
                {/* Tracer les lignes des targets */}
                {initialTargetLines.length > 0 &&
                    initialTargetLines.map((line, index) => {

                        return (
                            <line
                                key={index + 'target'}
                                x1={line.x1 + character.width / 2}  // Départ de la ligne
                                y1={line.y1 + character.height / 2} // Ajuster pour centrer
                                x2={line.x2 + character.width / 2}  // Fin de la ligne
                                y2={line.y2 + character.height / 2} // Ajuster pour centrer
                                stroke="lightgray"  // Couleur de la ligne
                                strokeWidth="5"  // Épaisseur de la ligne
                                strokeLinecap="round"  // Rendre les extrémités arrondies
                                strokeLinejoin="round"  // Rendre les jonctions arrondies
                                strokeDasharray={line.type}
                            />
                        );
                    })}
                {/* Tracer les lignes de l'objet */}
                {targetLines.length > 1 &&
                    targetLines.map((line, index) => {


                        return (
                            <line
                                key={index + 'result'}
                                x1={line.x1 + character.width / 2}  // Départ de la ligne
                                y1={line.y1 + character.height / 2} // Ajuster pour centrer
                                x2={line.x2 + character.width / 2}  // Fin de la ligne
                                y2={line.y2 + character.height / 2} // Ajuster pour centrer
                                stroke={line.color}  // Couleur de la ligne
                                strokeWidth="5"  // Épaisseur de la ligne
                                strokeLinecap="round"  // Rendre les extrémités arrondies
                                strokeLinejoin="round"  // Rendre les jonctions arrondies
                                strokeDasharray={line.type}
                            />
                        );
                    })}
            </svg>

            {/* Animation du personnage */}
            <motion.div
                id={"character"}
                style={{
                    width: `${character.width}px`,
                    height: `${character.height}px`,
                    backgroundImage: `url(${getCharacterImage()})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat', // Correction de la répétition d'image
                    position: 'absolute',
                    backgroundPosition: 'center',
                    border: editionMode ? '2px dashed blue' : 'none', // Bordure noire dashed si editionMode est vrai 
                }}
                animate={controls}
            />
        </div>
        </div>
        </div>
    );
};

export default IGame;
